import type { ReactNode, CSSProperties } from "react";
import { InfoTooltip } from './InfoTooltip';

/**
 * AccessoriesOptions - Accessories selection for the configurator
 * Visual card-style rows matching the configurator design language
 */

type AccessoriesOptionsProps = {
  title?: string;
  ledsVisible: boolean;
  onLedToggle: () => void;
  heaterQuantity: number;
  onHeaterQtyChange: (qty: number) => void;
};

const LightbulbIcon = ({ on }: { on: boolean }) => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke={on ? '#f5c542' : '#aaa'}
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 18h6" />
    <path d="M10 22h4" />
    <path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z" />
    {on && (
      <>
        <line x1="12" y1="2" x2="12" y2="0" stroke="#f5c542" strokeWidth="2" />
        <line x1="4.22" y1="4.22" x2="2.81" y2="2.81" stroke="#f5c542" strokeWidth="2" />
        <line x1="1" y1="12" x2="3" y2="12" stroke="#f5c542" strokeWidth="2" />
        <line x1="19.78" y1="4.22" x2="21.19" y2="2.81" stroke="#f5c542" strokeWidth="2" />
        <line x1="21" y1="12" x2="23" y2="12" stroke="#f5c542" strokeWidth="2" />
      </>
    )}
  </svg>
);

const imgStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: 10,
};

const ToggleRow = ({
  active,
  onClick,
  icon,
  label,
  description,
  disabled,
  tooltip,
}: {
  active: boolean;
  onClick: () => void;
  icon: ReactNode;
  label: string;
  description: string;
  disabled?: boolean;
  tooltip?: string;
}) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    style={{
      display: "flex",
      alignItems: "center",
      width: "100%",
      gap: 14,
      padding: "14px 16px",
      border: active ? "2px solid #71903e" : "1px solid #d9d9d9",
      borderRadius: 10,
      background: active ? "rgba(113,144,62,0.06)" : "#fff",
      cursor: disabled ? "default" : "pointer",
      transition: "all 0.2s",
      opacity: disabled ? 0.85 : 1,
    }}
  >
    <div
      style={{
        width: 48,
        height: 48,
        borderRadius: 10,
        background: active ? "rgba(140,167,255,0.14)" : "#f5f5f5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "background 0.2s",
      }}
    >
      {icon}
    </div>

    <div style={{ flex: 1, textAlign: "left" }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: "#333", display: "flex", alignItems: "center", gap: 6 }}>
        {label}
        {tooltip && (
          <span
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <InfoTooltip text={tooltip} />
          </span>
        )}
      </div>
      <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
        {description}
      </div>
    </div>

    <div
      style={{
        width: 44,
        height: 24,
        borderRadius: 12,
        background: active ? "#71903e" : "#ccc",
        position: "relative",
        flexShrink: 0,
        transition: "background 0.2s",
      }}
    >
      <div
        style={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: "#fff",
          position: "absolute",
          top: 3,
          left: active ? 23 : 3,
          transition: "left 0.2s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }}
      />
    </div>
  </button>
);

const QtyRow = ({
  qty,
  onQtyChange,
  icon,
  label,
  tooltip,
}: {
  qty: number;
  onQtyChange: (qty: number) => void;
  icon: ReactNode;
  label: string;
  tooltip?: string;
}) => {
  const active = qty > 0;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        gap: 14,
        padding: "14px 16px",
        border: active ? "2px solid #71903e" : "1px solid #d9d9d9",
        borderRadius: 10,
        background: active ? "rgba(113,144,62,0.06)" : "#fff",
        transition: "all 0.2s",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 10,
          background: active ? "rgba(140,167,255,0.14)" : "#f5f5f5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "background 0.2s",
        }}
      >
        {icon}
      </div>

      <div style={{ flex: 1, textAlign: "left" }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#333", display: "flex", alignItems: "center", gap: 6 }}>
          {label}
          {tooltip && <InfoTooltip text={tooltip} />}
        </div>
        <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
          {qty === 0 ? "None" : `Qty: ${qty}`}
        </div>
      </div>

      {/* Qty stepper */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <button
          type="button"
          onClick={() => onQtyChange(Math.max(0, qty - 1))}
          disabled={qty === 0}
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            border: "1.5px solid",
            borderColor: qty === 0 ? "#e0e0e0" : "#71903e",
            background: "#fff",
            color: qty === 0 ? "#ccc" : "#71903e",
            fontSize: 18,
            lineHeight: 1,
            cursor: qty === 0 ? "default" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            transition: "all 0.2s",
          }}
        >
          −
        </button>

        <span
          style={{
            minWidth: 20,
            textAlign: "center",
            fontSize: 15,
            fontWeight: 700,
            color: active ? "#333" : "#aaa",
          }}
        >
          {qty}
        </span>

        <button
          type="button"
          onClick={() => onQtyChange(qty + 1)}
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            border: "1.5px solid #71903e",
            background: "#71903e",
            color: "#fff",
            fontSize: 18,
            lineHeight: 1,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            transition: "all 0.2s",
          }}
        >
          +
        </button>
      </div>
    </div>
  );
};

export const AccessoriesOptions = ({
  title = 'Accessories',
  ledsVisible,
  onLedToggle,
  heaterQuantity,
  onHeaterQtyChange,
}: AccessoriesOptionsProps) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <p
        style={{
          textAlign: 'center',
          fontSize: 15,
          color: '#444',
          margin: '0 0 16px',
        }}
      >
        Choose your <strong>{title.toLowerCase()}</strong>:
      </p>

      <ToggleRow
        active={ledsVisible}
        onClick={onLedToggle}
        icon={<LightbulbIcon on={ledsVisible} />}
        label="Louver LED Lighting Kit"
        description={ledsVisible ? "Enabled" : "Disabled"}
        tooltip="Exclusive to the Signature Custom Pergola, this LED lighting kit integrates illumination into both the louvers and perimeter for full-structure lighting."
      />

      <QtyRow
        qty={heaterQuantity}
        onQtyChange={onHeaterQtyChange}
        icon={<img src="/images/heater.png" alt="Electric Heater" style={imgStyle} />}
        label="Electric Heater"
        tooltip="Installs directly into the pergola, delivering infrared warmth with remote control operation for easy comfort. Includes a 1500W output and standard plug connection."
      />
    </div>
  );
};
